/* Copy da página de adoção por versão (ADR-0820). “Cliques” é deliberado: o contador público
   inclui retries/bots e não representa instalações únicas. */
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

interface DownloadsContent {
  readonly pageTitle: string;
  readonly pageDescription: string;
  readonly refresh: string;
  readonly loadError: string;
  readonly retry: string;
  readonly emptyTitle: string;
  readonly emptyDescription: string;
  readonly semantics: string;
  readonly activityUnavailable: string;
  readonly updatedAt: string;
  readonly windowLabel: (hours: number) => string;
  readonly unknownVersion: string;
  readonly unknownChannel: string;
  readonly channels: {
    readonly stable: string;
    readonly beta: string;
    readonly canary: string;
  };
  readonly kpis: {
    readonly humanClicks: string;
    readonly humanClicksHint: string;
    readonly versions: string;
    readonly versionsHint: string;
    readonly activeUsers: string;
    readonly activeDevices: string;
  };
  readonly charts: {
    readonly clicksTitle: string;
    readonly activityTitle: string;
    readonly activeUsers: string;
    readonly activeDevices: string;
  };
  readonly detailsTitle: string;
  readonly columns: {
    readonly version: string;
    readonly channel: string;
    readonly humanClicks: string;
    readonly lastClick: string;
    readonly activeUsers: string;
    readonly activeDevices: string;
  };
  readonly never: string;
}

export const DOWNLOADS_CONTENT: Bilingual<DownloadsContent> = {
  'pt-BR': {
    pageTitle: 'Downloads',
    pageDescription: 'Adoção do aplicativo e cliques de download por versão.',
    refresh: 'Atualizar',
    loadError: 'Falha ao carregar downloads e adoção. Tente novamente.',
    retry: 'Tentar novamente',
    emptyTitle: 'Nenhuma versão observada',
    emptyDescription: 'As versões aparecerão após o primeiro registro ou heartbeat.',
    semantics:
      'Cliques humanos são requests acumulados e podem incluir tentativas repetidas ou bots. Usuários e dispositivos ativos consideram somente a última heartbeat da janela.',
    activityUnavailable:
      'A atividade por versão está indisponível no momento. Os cliques de download continuam atualizados; usuários e dispositivos aparecem como zero até a próxima atualização.',
    updatedAt: 'Atualizado em',
    windowLabel: (hours) => `Janela de atividade: ${hours}h`,
    unknownVersion: 'Versão desconhecida',
    unknownChannel: 'Sem canal registrado',
    channels: { stable: 'Estável', beta: 'Beta', canary: 'Canary' },
    kpis: {
      humanClicks: 'Cliques humanos',
      humanClicksHint: 'Acumulados em todos os canais',
      versions: 'Versões observadas',
      versionsHint: 'Com release ou atividade registrada',
      activeUsers: 'Usuários ativos',
      activeDevices: 'Dispositivos ativos',
    },
    charts: {
      clicksTitle: 'Cliques por versão e canal',
      activityTitle: 'Atividade por versão',
      activeUsers: 'Usuários ativos',
      activeDevices: 'Dispositivos ativos',
    },
    detailsTitle: 'Detalhamento por versão',
    columns: {
      version: 'Versão',
      channel: 'Canal',
      humanClicks: 'Cliques humanos acumulados',
      lastClick: 'Último clique',
      activeUsers: 'Usuários ativos (24h)',
      activeDevices: 'Dispositivos ativos (24h)',
    },
    never: 'Nunca',
  },
  'en-US': {
    pageTitle: 'Downloads',
    pageDescription: 'Application adoption and download clicks by version.',
    refresh: 'Refresh',
    loadError: 'Failed to load downloads and adoption. Try again.',
    retry: 'Try again',
    emptyTitle: 'No observed versions',
    emptyDescription: 'Versions will appear after their first registration or heartbeat.',
    semantics:
      'Human clicks are accumulated requests and may include retries or bots. Active users and devices use only the latest heartbeat in the window.',
    activityUnavailable:
      'Per-version activity is currently unavailable. Download clicks remain current; users and devices show as zero until the next refresh.',
    updatedAt: 'Updated at',
    windowLabel: (hours) => `Activity window: ${hours}h`,
    unknownVersion: 'Unknown version',
    unknownChannel: 'No registered channel',
    channels: { stable: 'Stable', beta: 'Beta', canary: 'Canary' },
    kpis: {
      humanClicks: 'Human clicks',
      humanClicksHint: 'Accumulated across all channels',
      versions: 'Observed versions',
      versionsHint: 'With a release or recorded activity',
      activeUsers: 'Active users',
      activeDevices: 'Active devices',
    },
    charts: {
      clicksTitle: 'Clicks by version and channel',
      activityTitle: 'Activity by version',
      activeUsers: 'Active users',
      activeDevices: 'Active devices',
    },
    detailsTitle: 'Details by version',
    columns: {
      version: 'Version',
      channel: 'Channel',
      humanClicks: 'Accumulated human clicks',
      lastClick: 'Last click',
      activeUsers: 'Active users (24h)',
      activeDevices: 'Active devices (24h)',
    },
    never: 'Never',
  },
};

export const useDownloadsContent = (): DownloadsContent => useBilingual(DOWNLOADS_CONTENT);
